import { tooltipFactory, TooltipProvider } from '@milkdown/kit/plugin/tooltip';
import {
    isInTable,
    addRowAfter,
    addColumnAfter,
    deleteRow as pmDeleteRow,
    deleteColumn as pmDeleteColumn,
    deleteTable as pmDeleteTable
} from '@milkdown/kit/prose/tables';
import type { Ctx } from '@milkdown/kit/ctx';
import {
    usePluginViewContext,
    usePluginViewFactory
} from '@prosemirror-adapter/react';
import {
    IconColumnInsertRight,
    IconRowInsertBottom,
    IconColumnRemove,
    IconRowRemove,
    IconTrash
} from '@tabler/icons-react';

import { useEffect, useRef } from 'react';

import { ActionIcon, Divider, Group, Tooltip } from '@mantine/core';

import './table-toolbar.css';

// Create the tooltip plugin pair
export const tableTooltip = tooltipFactory('table-toolbar');

function TableToolbarView() {
    const { view, prevState } = usePluginViewContext();
    const tooltipRef = useRef<HTMLDivElement>(null);
    const providerRef = useRef<TooltipProvider | null>(null);

    useEffect(() => {
        const el = tooltipRef.current;
        if (!el) return;

        const provider = new TooltipProvider({
            content: el,
            debounce: 50,
            shouldShow: (v) => {
                // Only show when cursor is in a table with an empty selection
                // (non-empty selection shows the formatting tooltip instead)
                if (!v.state.selection.empty) return false;
                return isInTable(v.state);
            },
            offset: { mainAxis: 8 }
        });

        providerRef.current = provider;
        return () => {
            provider.destroy();
            providerRef.current = null;
        };
    }, []);

    useEffect(() => {
        providerRef.current?.update(view, prevState);
    });

    const run = (cmd: (state: typeof view.state, dispatch?: typeof view.dispatch) => boolean) => {
        cmd(view.state, view.dispatch);
        view.focus();
    };

    // Prevent mousedown on toolbar from stealing editor focus/selection
    const keepFocus = (e: React.MouseEvent) => e.preventDefault();

    return (
        <div ref={tooltipRef} className="milkdown-table-toolbar" role="toolbar" aria-label="Table toolbar" onMouseDown={keepFocus}>
            <Group gap={2}>
                <Tooltip label="Add row below" position="top" withArrow>
                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => run(addRowAfter)}>
                        <IconRowInsertBottom size={16} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Add column right" position="top" withArrow>
                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => run(addColumnAfter)}>
                        <IconColumnInsertRight size={16} />
                    </ActionIcon>
                </Tooltip>

                <Divider orientation="vertical" />

                <Tooltip label="Delete row" position="top" withArrow>
                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => run(pmDeleteRow)}>
                        <IconRowRemove size={16} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Delete column" position="top" withArrow>
                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => run(pmDeleteColumn)}>
                        <IconColumnRemove size={16} />
                    </ActionIcon>
                </Tooltip>

                <Divider orientation="vertical" />

                <Tooltip label="Delete table" position="top" withArrow>
                    <ActionIcon variant="subtle" color="red" size="sm" onClick={() => run(pmDeleteTable)}>
                        <IconTrash size={16} />
                    </ActionIcon>
                </Tooltip>
            </Group>
        </div>
    );
}

/**
 * Call from MilkdownEditorInner to wire the table toolbar plugin.
 */
export function useTableToolbar() {
    const pluginViewFactory = usePluginViewFactory();

    return (ctx: Ctx) => {
        ctx.set(tableTooltip.key, {
            view: pluginViewFactory({ component: TableToolbarView })
        });
    };
}
